import * as React from 'react'
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js'
import { Combobox, type ComboboxOption } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { internationalCountries, toCountryCode } from '@/lib/countries'

const callingCodeOptions: ComboboxOption[] = internationalCountries.map((country) => {
  return {
    value: country.code,
    label: `${country.name} (+${country.callingCode})`,
    selectedLabel: `+${country.callingCode}`,
    keywords: `${country.code} ${country.callingCode}`,
  }
})

type Props = {
  id: string
  value: string
  onValueChange: (value: string) => void
  defaultCountryCode?: string | null
  disabled?: boolean
  ariaInvalid?: boolean
  ariaDescribedBy?: string
  ariaLabel?: string
  placeholder?: string
  className?: string
}

type SelectedCountry = CountryCode | ''

function countryFromValue(value: string, fallback?: string | null): SelectedCountry {
  const parsedCountry = value.trim().startsWith('+') ? parsePhoneNumberFromString(value)?.country : undefined
  return parsedCountry ?? toCountryCode(fallback) ?? ''
}

function nationalNumberFromValue(value: string): string {
  if (!value) return ''

  const parsed = value.trim().startsWith('+') ? parsePhoneNumberFromString(value) : undefined
  return parsed ? parsed.formatNational() : value
}

function internationalValue(nationalNumber: string, country: SelectedCountry): string {
  const digits = nationalNumber.replace(/\D/g, '')
  if (!digits) return ''
  if (!country) return nationalNumber

  return parsePhoneNumberFromString(nationalNumber, country)?.number
    ?? `+${getCountryCallingCode(country)}${digits}`
}

export function InternationalPhoneInput({
  id,
  value,
  onValueChange,
  defaultCountryCode,
  disabled,
  ariaInvalid,
  ariaDescribedBy,
  ariaLabel,
  placeholder = 'Phone number',
  className,
}: Props) {
  const [country, setCountry] = React.useState<SelectedCountry>(() => countryFromValue(value, defaultCountryCode))
  const [nationalNumber, setNationalNumber] = React.useState(() => nationalNumberFromValue(value))

  React.useEffect(() => {
    const nextCountry = countryFromValue(value, defaultCountryCode)
    const nextNationalNumber = nationalNumberFromValue(value)
    setCountry(nextCountry)
    setNationalNumber(nextNationalNumber)
  }, [defaultCountryCode, value])

  const changeCountry = (nextValue: string) => {
    const nextCountry = toCountryCode(nextValue) ?? 'US'
    setCountry(nextCountry)
    onValueChange(internationalValue(nationalNumber, nextCountry))
  }

  const changeNationalNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value
    if (rawValue.trim().startsWith('+')) {
      const parsed = parsePhoneNumberFromString(rawValue)
      if (parsed) {
        if (parsed.country) setCountry(parsed.country)
        setNationalNumber(parsed.formatNational())
        onValueChange(parsed.number)
        return
      }
    }

    const nextNationalNumber = rawValue.replace(/[^\d\s().-]/g, '')
    setNationalNumber(nextNationalNumber)
    onValueChange(internationalValue(nextNationalNumber, country))
  }

  return (
    <div className={cn('flex w-full', className)}>
      <Combobox
        id={`${id}-country-code`}
        options={callingCodeOptions}
        value={country}
        onValueChange={changeCountry}
        ariaLabel="Country calling code"
        ariaInvalid={ariaInvalid}
        ariaDescribedBy={ariaDescribedBy}
        disabled={disabled}
        className="w-28 shrink-0 rounded-r-none border-r-0 focus-visible:z-10"
        contentClassName="w-64 max-w-[calc(100vw-2rem)]"
        placeholder="Code"
        searchPlaceholder="Search country or code…"
        emptyMessage="No calling code found."
      />
      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={nationalNumber}
        onChange={changeNationalNumber}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        placeholder={placeholder}
        className="rounded-l-none focus-visible:z-10"
      />
    </div>
  )
}
